/**
 * Table utility functions for VirtualizedPersonaResults
 */

/**
 * Format currency value for display
 * @param {number|string} value - Revenue value
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value) => {
  if (!value) return 'N/A';
  if (typeof value === 'number') {
    return `$${value.toFixed(1)}M`;
  }
  return String(value);
};

/**
 * Format employee count for display
 * @param {number|string} value - Employee count
 * @returns {string} - Formatted employee count string
 */
export const formatEmployees = (value) => {
  if (!value) return 'N/A';
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  return String(value);
};

/**
 * Format custom field value for display (simple string version)
 * Used for tooltips and non-rich text contexts
 * @param {*} value - The value to format
 * @returns {string} - Formatted value string
 */
export const formatCustomFieldValue = (value) => {
  // Check for null/undefined explicitly
  if (value === null || value === undefined) {
    return 'N/A';
  }
  
  // Handle numbers (including 0)
  if (typeof value === 'number') {
    return String(value);
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) return 'N/A';
    // Format array items and join with commas
    return value.map(item => 
      typeof item === 'object' ? JSON.stringify(item) : String(item)
    ).join(', ');
  }
  
  // Handle objects
  if (typeof value === 'object') {
    // Check for empty object
    if (Object.keys(value).length === 0) return 'N/A';
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return '[Object]';
    }
  }
  
  // Handle strings (including empty strings)
  if (typeof value === 'string') {
    return value.trim() === '' ? 'N/A' : value;
  }
  
  // Fallback
  return String(value);
};

/**
 * Get the data type of a custom field value
 * @param {*} value - The value to check
 * @returns {string} - Type identifier: 'null', 'boolean', 'number', 'string', 'array', 'array-of-objects', 'object'
 */
export const getCustomFieldValueType = (value) => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') return 'string';
  if (Array.isArray(value)) {
    if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
      return 'array-of-objects';
    }
    return 'array';
  }
  if (typeof value === 'object') return 'object';
  return 'unknown';
};

/**
 * Get column configurations for company table
 * @param {number} customFieldCount - Number of custom fields
 * @returns {Object} - Column configuration object
 */
export const getCompanyColumns = (customFieldCount) => {
  const baseColumns = {
    checkbox: { width: '60px', flexGrow: 0, maxWidth: '60px' },
    name: { width: '300px', flexGrow: 0, maxWidth: '450px' },
    headcount: { width: '120px', flexGrow: 0, maxWidth: '140px' },
    revenue: { width: '120px', flexGrow: 0, maxWidth: '140px' },
    location: { width: '220px', flexGrow: 0, maxWidth: '350px' },
  };
  
  // Add custom field columns with wider width for better content display
  // Custom fields should have consistent max width to keep table uniform
  for (let i = 0; i < customFieldCount; i++) {
    baseColumns[`custom_${i}`] = { width: '280px', flexGrow: 0, maxWidth: '500px' };
  }
  
  baseColumns.links = { width: '150px', flexGrow: 0, maxWidth: '300px' };
  
  return baseColumns;
};

/**
 * Calculate uniform column widths for company table based on available space
 * @param {number} customFieldCount - Number of custom fields
 * @param {number} availableWidth - Available width in pixels
 * @returns {Object} - Column configuration with calculated widths
 */
export const calculateCompanyColumnWidths = (customFieldCount, availableWidth) => {
  const baseColumns = getCompanyColumns(customFieldCount);
  const columnKeys = Object.keys(baseColumns);
  
  // Calculate dynamic max widths as a percentage of available width
  // These percentages ensure columns grow proportionally with screen size
  const dynamicMaxWidths = {
    checkbox: 60, // Fixed
    name: Math.max(450, availableWidth * 0.15), // 15% of screen width, min 450px
    headcount: 140, // Fixed
    revenue: 140, // Fixed
    location: Math.max(350, availableWidth * 0.12), // 12% of screen width, min 350px
    links: Math.max(300, availableWidth * 0.10), // 10% of screen width, min 300px
  };
  
  // Custom fields get proportional max width
  for (let i = 0; i < customFieldCount; i++) {
    dynamicMaxWidths[`custom_${i}`] = Math.max(500, availableWidth * 0.18); // 18% of screen width, min 500px
  }
  
  // Start with minimum widths
  const calculatedWidths = {};
  columnKeys.forEach(key => {
    calculatedWidths[key] = parseInt(baseColumns[key].width);
  });
  
  // Calculate minimum total width
  let totalWidth = columnKeys.reduce((sum, key) => sum + calculatedWidths[key], 0);
  
  // Identify flexible columns (those that can grow)
  const flexibleColumns = ['name', 'location', 'links'];
  for (let i = 0; i < customFieldCount; i++) {
    flexibleColumns.push(`custom_${i}`);
  }
  
  // Iteratively distribute extra space, removing columns that hit their max
  if (availableWidth > totalWidth) {
    let remainingSpace = availableWidth - totalWidth;
    let activeFlexColumns = [...flexibleColumns];
    
    // Keep distributing until no space left or no columns can grow
    while (remainingSpace > 0 && activeFlexColumns.length > 0) {
      const spacePerColumn = Math.floor(remainingSpace / activeFlexColumns.length);
      
      if (spacePerColumn < 1) break; // Can't distribute fractions of pixels meaningfully
      
      let spaceUsed = 0;
      const columnsToRemove = [];
      
      activeFlexColumns.forEach(key => {
        const currentWidth = calculatedWidths[key];
        const maxWidth = dynamicMaxWidths[key];
        const spaceAvailable = maxWidth - currentWidth;
        
        if (spaceAvailable <= 0) {
          // Already at max, remove from active columns
          columnsToRemove.push(key);
        } else if (spaceAvailable >= spacePerColumn) {
          // Can take full allocation
          calculatedWidths[key] = currentWidth + spacePerColumn;
          spaceUsed += spacePerColumn;
        } else {
          // Can only take partial allocation (hit max)
          calculatedWidths[key] = maxWidth;
          spaceUsed += spaceAvailable;
          columnsToRemove.push(key);
        }
      });
      
      // Remove columns that hit their max
      activeFlexColumns = activeFlexColumns.filter(key => !columnsToRemove.includes(key));
      
      remainingSpace -= spaceUsed;
      
      // Prevent infinite loop
      if (spaceUsed === 0) break;
    }
  }
  
  // Apply calculated widths to all columns
  columnKeys.forEach(key => {
    baseColumns[key] = {
      ...baseColumns[key],
      width: `${calculatedWidths[key]}px`,
      maxWidth: `${calculatedWidths[key]}px`, // Lock to calculated width
      flexGrow: 0,
      flexShrink: 0
    };
  });
  
  return baseColumns;
};

/**
 * Get column configurations for contact table
 * @param {number} customFieldCount - Number of custom fields
 * @returns {Object} - Column configuration object
 */
export const getContactColumns = (customFieldCount, includeAddress = false) => {
  const baseColumns = {
    checkbox: { width: '60px', flexGrow: 0, maxWidth: '60px' },
    title: { width: '240px', flexGrow: 0, maxWidth: '350px' },
    company: { width: '260px', flexGrow: 0, maxWidth: '400px' },
    name: { width: '200px', flexGrow: 0, maxWidth: '300px' },
    linkedin: { width: '200px', flexGrow: 0, maxWidth: '280px' },
    email: { width: '200px', flexGrow: 0, maxWidth: '300px' },
    phone: { width: '200px', flexGrow: 0, maxWidth: '200px' },
    // User-managed tracking columns
    status: { width: '120px', flexGrow: 0, maxWidth: '120px' },
    notes: { width: '220px', flexGrow: 0, maxWidth: '400px' },
  };
  
  // Add custom field columns with appropriate widths
  // All custom fields get the same maxWidth for uniformity
  for (let i = 0; i < customFieldCount; i++) {
    baseColumns[`custom_${i}`] = { 
      width: i === 0 ? '280px' : '400px', 
      flexGrow: 0,
      maxWidth: '500px'
    };
  }
  
  // Location column gets larger minimum width to display full addresses
  baseColumns.location = { width: '280px', flexGrow: 0, maxWidth: '450px' };
  // Address column (Versium postal address) only appears once the campaign has
  // unlocked at least one address, so empty campaigns don't show a dead column.
  if (includeAddress) {
    baseColumns.address = { width: '300px', flexGrow: 0, maxWidth: '500px' };
  }
  return baseColumns;
};

/**
 * Calculate uniform column widths for contact table based on available space
 * @param {number} customFieldCount - Number of custom fields
 * @param {number} availableWidth - Available width in pixels
 * @returns {Object} - Column configuration with calculated widths
 */
export const calculateContactColumnWidths = (customFieldCount, availableWidth, includeAddress = false) => {
  const baseColumns = getContactColumns(customFieldCount, includeAddress);
  const columnKeys = Object.keys(baseColumns);
  
  // Calculate dynamic max widths as a percentage of available width
  // These percentages ensure columns grow proportionally with screen size
  const dynamicMaxWidths = {
    checkbox: 60, // Fixed
    title: Math.max(350, availableWidth * 0.12), // 12% of screen width, min 350px
    company: Math.max(400, availableWidth * 0.14), // 14% of screen width, min 400px
    name: Math.max(300, availableWidth * 0.10), // 10% of screen width, min 300px
    linkedin: Math.max(280, availableWidth * 0.09), // 9% of screen width, min 280px
    email: Math.max(300, availableWidth * 0.10), // 10% of screen width, min 300px
    phone: Math.max(200, availableWidth * 0.07), // 7% of screen width, min 200px
    status: 120, // Fixed
    notes: Math.max(400, availableWidth * 0.12), // 12% of screen width, min 400px
    location: Math.max(450, availableWidth * 0.15), // 15% of screen width, min 450px
    address: Math.max(500, availableWidth * 0.15), // 15% of screen width, min 500px
  };
  
  // Custom fields get proportional max width
  for (let i = 0; i < customFieldCount; i++) {
    dynamicMaxWidths[`custom_${i}`] = Math.max(500, availableWidth * 0.18); // 18% of screen width, min 500px
  }
  
  // Start with minimum widths
  const calculatedWidths = {};
  columnKeys.forEach(key => {
    calculatedWidths[key] = parseInt(baseColumns[key].width);
  });
  
  // Calculate minimum total width
  let totalWidth = columnKeys.reduce((sum, key) => sum + calculatedWidths[key], 0);
  
  // Identify flexible columns (those that can grow)
  const flexibleColumns = ['title', 'company', 'name', 'linkedin', 'email', 'phone', 'notes', 'location', 'address'];
  for (let i = 0; i < customFieldCount; i++) {
    flexibleColumns.push(`custom_${i}`);
  }
  
  // Iteratively distribute extra space, removing columns that hit their max
  if (availableWidth > totalWidth) {
    let remainingSpace = availableWidth - totalWidth;
    let activeFlexColumns = [...flexibleColumns];
    
    // Keep distributing until no space left or no columns can grow
    while (remainingSpace > 0 && activeFlexColumns.length > 0) {
      const spacePerColumn = Math.floor(remainingSpace / activeFlexColumns.length);
      
      if (spacePerColumn < 1) break; // Can't distribute fractions of pixels meaningfully
      
      let spaceUsed = 0;
      const columnsToRemove = [];
      
      activeFlexColumns.forEach(key => {
        const currentWidth = calculatedWidths[key];
        const maxWidth = dynamicMaxWidths[key];
        const spaceAvailable = maxWidth - currentWidth;
        
        if (spaceAvailable <= 0) {
          // Already at max, remove from active columns
          columnsToRemove.push(key);
        } else if (spaceAvailable >= spacePerColumn) {
          // Can take full allocation
          calculatedWidths[key] = currentWidth + spacePerColumn;
          spaceUsed += spacePerColumn;
        } else {
          // Can only take partial allocation (hit max)
          calculatedWidths[key] = maxWidth;
          spaceUsed += spaceAvailable;
          columnsToRemove.push(key);
        }
      });
      
      // Remove columns that hit their max
      activeFlexColumns = activeFlexColumns.filter(key => !columnsToRemove.includes(key));
      
      remainingSpace -= spaceUsed;
      
      // Prevent infinite loop
      if (spaceUsed === 0) break;
    }
  }
  
  // Apply calculated widths to all columns
  columnKeys.forEach(key => {
    baseColumns[key] = {
      ...baseColumns[key],
      width: `${calculatedWidths[key]}px`,
      maxWidth: `${calculatedWidths[key]}px`, // Lock to calculated width
      flexGrow: 0,
      flexShrink: 0
    };
  });
  
  return baseColumns;
};

/**
 * Calculate minimum table width for company table
 * @param {number} customFieldCount - Number of custom fields
 * @returns {number} - Minimum width in pixels
 */
export const getCompanyTableMinWidth = (customFieldCount) => {
  const columns = getCompanyColumns(customFieldCount);
  return Object.values(columns).reduce((sum, col) => sum + parseInt(col.width), 0);
};

/**
 * Calculate minimum table width for contact table
 * @param {number} customFieldCount - Number of custom fields
 * @returns {number} - Minimum width in pixels
 */
export const getContactTableMinWidth = (customFieldCount, includeAddress = false) => {
  const columns = getContactColumns(customFieldCount, includeAddress);
  return Object.values(columns).reduce((sum, col) => sum + parseInt(col.width), 0);
};

/**
 * Convert column config to style props
 * @param {Object} config - Column configuration
 * @returns {Object} - Style properties
 */
export const getColumnStyle = (config) => ({
  minWidth: config.width,
  maxWidth: config.maxWidth || (config.flexGrow === 0 ? config.width : 'none'),
  width: config.flexGrow === 0 ? config.width : 'auto',
  flexShrink: 0,
  flexGrow: config.flexGrow || (config.width === 'auto' ? 1 : 0),
  overflow: 'hidden', // Hide overflow - text will be truncated with ellipsis
});

/**
 * Constants for consistent heights
 */
export const ROW_HEIGHT = 80;
export const MIN_ROW_HEIGHT = 80;
export const MAX_ROW_HEIGHT = 200; // Constant max height to keep cell sizing sane

/**
 * Calculate dynamic row height based on content
 * @param {Object} rowData - Row data object
 * @param {Array} customFieldDefinitions - Custom field definitions
 * @returns {number} - Row height in pixels
 */
export const calculateRowHeight = (rowData, customFieldDefinitions = []) => {
  if (!rowData || rowData.type === 'empty_state') {
    return MIN_ROW_HEIGHT; // Default height for empty states
  }
  
  let maxHeight = MIN_ROW_HEIGHT;
  const lineHeight = 20; // Approximate line height in pixels
  const padding = 24; // Top and bottom padding
  
  // Check custom fields for content that might need more height
  if (rowData.data?.custom_fields && customFieldDefinitions.length > 0) {
    customFieldDefinitions.forEach(fieldDef => {
      const value = rowData.data.custom_fields[fieldDef.field_id];
      
      if (value === null || value === undefined || value === '') {
        return; // Skip null/undefined/empty values
      }
      
      // Estimate lines needed based on content length and type
      if (typeof value === 'object' && !Array.isArray(value)) {
        // Objects formatted as JSON need more space
        const jsonString = JSON.stringify(value, null, 2);
        const lines = Math.min(jsonString.split('\n').length, 8); // Cap at 8 lines
        const estimatedHeight = (lines * lineHeight) + padding;
        maxHeight = Math.max(maxHeight, estimatedHeight);
      } else if (Array.isArray(value) && value.length > 0) {
        // Arrays - estimate based on combined item length
        const arrayString = value.map(item => 
          typeof item === 'object' ? JSON.stringify(item) : String(item)
        ).join(', ');
        const maxColumnWidth = 500; // Use max width constraint
        const charsPerLine = Math.floor(maxColumnWidth / 8);
        const lines = Math.min(Math.ceil(arrayString.length / charsPerLine), 8); // Cap at 8 lines
        const estimatedHeight = (lines * lineHeight) + padding;
        maxHeight = Math.max(maxHeight, estimatedHeight);
      } else {
        // String or primitive values
        const formattedValue = formatCustomFieldValue(value);
        if (typeof formattedValue === 'string' && formattedValue.length > 50) {
          const maxColumnWidth = 500; // Use max width constraint
          const charsPerLine = Math.floor(maxColumnWidth / 8);
          const lines = Math.min(Math.ceil(formattedValue.length / charsPerLine), 8); // Cap at 8 lines
          const estimatedHeight = (lines * lineHeight) + padding;
          maxHeight = Math.max(maxHeight, estimatedHeight);
        }
      }
    });
  }
  
  return Math.max(MIN_ROW_HEIGHT, Math.min(maxHeight, MAX_ROW_HEIGHT)); // Cap between 80-200px
};

/**
 * Legacy function for backward compatibility
 * @returns {number} - Row height in pixels
 */
export const getRowHeight = () => {
  return ROW_HEIGHT;
};

/**
 * Row types for tab-based structure
 */
export const ROW_TYPES = {
  COMPANY: 'company',
  CONTACT: 'contact',
  EMPTY_STATE: 'empty_state'
};

/**
 * Whether to show Subject column for a draft.
 * Returns false when:
 *   - the message type has no concept of a subject (LinkedIn message / connection), OR
 *   - both subjectTemplate and subjectFieldId are empty/null (nothing to display)
 */
export const shouldShowSubjectColumn = (draft) => {
  const typeOfMessage = draft?.typeOfMessage ?? (draft?.messageType === 'linkedin' ? 'linkedin' : 'email');
  const messageType = draft?.messageType ?? 'new_thread';
  const noSubjectTypes = ['message', 'connection_message', 'linkedin'];
  if (typeOfMessage === 'linkedin' && noSubjectTypes.includes(messageType)) return false;
  // Hide the column entirely when there is no subject content at all
  return !!(draft?.subjectTemplate || draft?.subjectFieldId);
};

/**
 * Get column configurations for message columns.
 * Subject column omitted for LinkedIn Message/Connection message.
 * @param {Array} messageDrafts - Message drafts (each may have typeOfMessage, messageType)
 * @returns {Object} - Column configuration object
 */
export const getMessageColumns = (messageDrafts) => {
  const drafts = Array.isArray(messageDrafts) ? messageDrafts : [];
  const columns = {};
  for (let i = 0; i < drafts.length; i++) {
    if (shouldShowSubjectColumn(drafts[i])) {
      columns[`message_${i}_subject`] = { width: '280px', flexGrow: 0, maxWidth: '400px' };
    }
    columns[`message_${i}_body`] = { width: '280px', flexGrow: 0, maxWidth: '400px' };
    columns[`message_${i}_smartlead_exports`] = { width: '170px', flexGrow: 0, maxWidth: '220px' };
    columns[`message_${i}_heyreach_exports`] = { width: '170px', flexGrow: 0, maxWidth: '220px' };
    columns[`message_${i}_gillyreach_exports`] = { width: '200px', flexGrow: 0, maxWidth: '250px' };
  }
  return columns;
};

/**
 * Calculate total width for message columns.
 * Subject (280px) only when shouldShowSubjectColumn; Body (280px) always.
 * @param {Array} messageDrafts - Message drafts
 * @param {number} smartleadExportedCount - Number of groups exported to Smartlead
 * @param {number} heyreachExportedCount - Number of groups exported to HeyReach
 */
export const getMessageColumnsWidth = (
  messageDrafts,
  smartleadExportedCount = 0,
  heyreachExportedCount = 0,
  gillyreachExportedCount = 0
) => {
  const drafts = Array.isArray(messageDrafts) ? messageDrafts : [];
  const baseWidth = drafts.reduce((sum, d) => sum + (shouldShowSubjectColumn(d) ? 560 : 280), 0);
  return baseWidth + smartleadExportedCount * 170 + heyreachExportedCount * 170 + gillyreachExportedCount * 200;
};

// Map from display label → contact field key (mirrors AVAILABLE_VARIABLES in MessageDraftingPanel)
const LABEL_TO_FIELD_KEY = {
  'First Name': 'first_name',
  'Last Name': 'last_name',
  'Position': 'position',
  'Company Name': 'company_name',
  'Location': 'location',
  'Email': 'email',
  'Industry': 'company_industry',
  'Website': 'company_website',
};

const applyCleanFormat = (value, format, removeEmojis) => {
  let v = (value || '').trim();
  if (!v) return v;

  if (removeEmojis === 'true') {
    // Strip emoji and non-ASCII special characters
    v = v.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '').trim();
  }

  switch (format) {
    case 'Title Case':
      v = v.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
      break;
    case 'lower case':
      v = v.toLowerCase();
      break;
    case 'UPPER CASE':
      v = v.toUpperCase();
      break;
    case 'Sentence case':
      v = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
      break;
    default:
      break;
  }
  return v;
};

const fieldKeyFromColumn = (label) => {
  const trimmed = (label || '').trim();
  // 1. Display label e.g. "First Name"
  if (LABEL_TO_FIELD_KEY[trimmed]) return LABEL_TO_FIELD_KEY[trimmed];
  // 2. {{key}} syntax e.g. "{{first_name}}"
  const m = trimmed.match(/^\{\{(.+?)\}\}$/);
  if (m) return m[1].trim();
  // 3. Raw key e.g. "first_name"
  return trimmed;
};

/**
 * Resolve template variables with contact data.
 * Handles both plain {{key}} tokens and {{CLEAN|label|format|removeEmojis|fallback}} tags.
 */
export const resolveTemplateVariables = (template, contact) => {
  if (!template || !contact) return template || '';

  // Resolve CLEAN tags first (non-greedy fallback to handle any content)
  let resolved = template.replace(
    /\{\{CLEAN\|([^|]*)\|([^|]*)\|([^|]*)\|(.*?)\}\}/gs,
    (_, label, format, removeEmojis, fallback) => {
      const fieldKey = fieldKeyFromColumn(label);
      const raw = contact[fieldKey] || '';
      const cleaned = applyCleanFormat(raw, format.trim(), removeEmojis.trim());
      return cleaned || fallback.trim() || '';
    }
  );

  return resolved
    .replace(/\{\{first_name\}\}/g, contact.first_name || '')
    .replace(/\{\{last_name\}\}/g, contact.last_name || '')
    .replace(/\{\{company_name\}\}/g, contact.company_name || '')
    .replace(/\{\{position\}\}/g, contact.position || '')
    .replace(/\{\{email\}\}/g, contact.email || '')
    .replace(/\{\{location\}\}/g, contact.location || '')
    .replace(/\{\{linkedin\}\}/g, contact.linkedin || '')
    .replace(/\{\{company_industry\}\}/g, contact.company_industry || contact.industry || '')
    .replace(/\{\{company_website\}\}/g, contact.company_website || contact.website || '');
};

